/**
 * ============================================================================
 * inviteService.ts - Handle Player Invites with Secure Token Links
 * ============================================================================
 * 
 * PRODUCTION-READY invite system with secure token-based links.
 * 
 * Features:
 * - Generate secure invite tokens via backend (24-hour expiry)
 * - Token-based links work for NEW users (signup → onboarding → game)
 * - Token-based links work for EXISTING users (login → game)
 * - Works for both PUBLIC and PRIVATE tables
 * - Private table access granted via token (no code needed)
 * - Rate limiting to prevent spam
 * - In-app invites to specific users
 * 
 * Flow for invite links:
 * 1. User A generates invite link from game
 * 2. User B clicks link → lands on /join/:token
 * 3. InviteLandingPage validates token
 * 4. If logged in → redirect to game
 * 5. If not logged in → store token → redirect to login
 * 6. After login/signup → check for pending invite → redirect to game
 * 7. New users go through onboarding first, then to game
 * 
 * ============================================================================
 */

import {
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { isUserOnline } from './presenceService';
import { getUserEmail } from './socialService';
import { API_BASE } from '../utils/apiBase';

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

// ============================================================================
// RATE LIMITING (Client-side)
// ============================================================================

const INVITE_COOLDOWN_MS = 10000; // 10 seconds between invites to same user
const lastInviteTimes = new Map<string, number>();

// Cleanup old entries every 60 seconds
setInterval(() => {
  const now = Date.now();
  for (const [key, time] of lastInviteTimes.entries()) {
    if (now - time > 60000) {
      lastInviteTimes.delete(key);
    }
  }
}, 60000);

// ============================================================================
// TYPES
// ============================================================================

export interface TableInvite {
  id: string;
  fromUID: string;
  fromUserName: string;
  toUID: string;
  toEmail?: string;
  tableId: string;
  tableName: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  createdAt: Timestamp | null;
  expiresAt: Timestamp | null;
  recipientWasOffline?: boolean;
  emailSent?: boolean;
  smsSent?: boolean;
  /** Group game invites (written by the server) carry these; accept opens `url`. */
  kind?: 'table' | 'group_event' | 'group';
  url?: string;
  groupId?: string;
  groupName?: string;
  eventId?: string;
}

export interface InviteTokenData {
  token: string;
  tableId: string;
  tableName: string;
  expiresAt: string;
  shareUrl: string;
}

export interface TokenValidationResult {
  valid: boolean;
  tableId?: string;
  tableName?: string;
  isPrivate?: boolean;
  currency?: 'playcoins' | 'pkcash';
  smallBlind?: number;
  bigBlind?: number;
  minBuyIn?: number;
  maxBuyIn?: number;
  currentPlayers?: number;
  maxSeats?: number;
  isFull?: boolean;
  expiresAt?: string;
  error?: string;
  /** Host's referral code: a friend who signs up from this invite is their referral. */
  inviterReferralCode?: string | null;
  inviterName?: string | null;
}

export interface UseTokenResult {
  success: boolean;
  tableId?: string;
  tableName?: string;
  privateCode?: string | null;
  currency?: string;
  minBuyIn?: number;
  maxBuyIn?: number;
  error?: string;
}

// ============================================================================
// LOCAL STORAGE KEYS
// ============================================================================

const STORAGE_KEYS = {
  PENDING_INVITE_TOKEN: 'pendingInviteToken',
  PENDING_INVITE_TABLE_ID: 'pendingInviteTableId',
  POST_ONBOARDING_REDIRECT: 'postOnboardingRedirect',
} as const;

// ============================================================================
//  SECURE INVITE TOKEN FUNCTIONS

// ============================================================================

/**
 * Generate a secure invite token for a table
 * - Works for public AND private tables
 * - Token grants access to private tables (no code needed)
 * - Expires in 24 hours
 * - Requires user to be at the table or be the owner
 */
export const generateSecureInviteToken = async (
  tableId: string
): Promise<InviteTokenData> => {
  try {
    const authToken = await getAuthToken();
    
    const response = await fetch(`${API_BASE}/generateInviteToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ tableId }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to generate invite link');
    }

    // Generate the shareable URL
    const shareUrl = `${window.location.origin}/join/${data.token}`;

    console.log('🔗 [INVITE] Generated secure invite token for table:', tableId);

    return {
      token: data.token,
      tableId: data.tableId,
      tableName: data.tableName,
      expiresAt: data.expiresAt,
      shareUrl,
    };
  } catch (error) {
    console.error('Error generating invite token:', error);
    throw error;
  }
};

/**
 * Validate an invite token (PUBLIC - no auth required)
 * - Called by InviteLandingPage to show table info
 * - Does NOT consume the token
 */
export const validateInviteToken = async (token: string): Promise<TokenValidationResult> => {
  try {
    if (!token || token.length < 32) {
      return { valid: false, error: 'Invalid invite link format' };
    }

    const response = await fetch(`${API_BASE}/validateInviteToken/${token}`);
    const data = await response.json();
    
    return data;
  } catch (error) {
    console.error('Error validating invite token:', error);
    return { valid: false, error: 'Could not validate invite link' };
  }
};

/**
 * Consume an invite token to get access to a table
 * - Requires authentication
 * - Returns private code for private tables
 * - Records token usage for analytics
 */
export const consumeInviteToken = async (token: string): Promise<UseTokenResult> => {
  try {
    if (!token || token.length < 32) {
      return { success: false, error: 'Invalid token' };
    }

    const authToken = await getAuthToken();
    
    const response = await fetch(`${API_BASE}/useInviteToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ token }),
    });

    const data = await response.json();
    
    if (data.success) {
      console.log('✅ [INVITE] Token accepted - access granted to table:', data.tableId);
    }
    
    return data;
  } catch (error) {
    console.error('Error using invite token:', error);
    return { success: false, error: 'Could not use invite link' };
  }
};

/**
 * Copy secure invite link to clipboard
 * - Generates a new token and copies the shareable message
 */
export const copySecureInviteLink = async (
  tableId: string,
  tableName: string
): Promise<boolean> => {
  try {
    const tokenData = await generateSecureInviteToken(tableId);
    
    const message = `Join me at ${tableName}!\n\n${tokenData.shareUrl}`;

    
    await navigator.clipboard.writeText(message);
    console.log('✅ [INVITE] Link copied to clipboard');
    return true;
  } catch (error) {
    console.error('Error copying secure invite link:', error);
    return false;
  }
};

/**
 * Share secure invite link via native share API (mobile)
 * - Generates a new token and shares via OS share sheet
 */
export const shareSecureInviteNative = async (
  tableId: string,
  tableName: string
): Promise<boolean> => {
  try {
    const tokenData = await generateSecureInviteToken(tableId);
    
    if (navigator.share) {
      await navigator.share({
        title: `Join ${tableName}`,
        text: `Join me for poker at ${tableName}! `,

        url: tokenData.shareUrl,
      });
      return true;
    }
    
    // Fallback to clipboard
    return await copySecureInviteLink(tableId, tableName);
  } catch (error) {
    // User may have cancelled share dialog
    if ((error as Error).name !== 'AbortError') {
      console.error('Error sharing:', error);
    }
    return false;
  }
};

// ============================================================================
// PENDING INVITE STORAGE (for post-login/signup redirect)
// ============================================================================

/**
 * Store a pending invite token for after login/signup
 */
export const storePendingInvite = (token: string, tableId: string): void => {
  localStorage.setItem(STORAGE_KEYS.PENDING_INVITE_TOKEN, token);
  localStorage.setItem(STORAGE_KEYS.PENDING_INVITE_TABLE_ID, tableId);
  console.log('💾 [INVITE] Stored pending invite for post-login redirect');
};

/**
 * Get the pending invite token from localStorage
 */
export const getPendingInviteToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.PENDING_INVITE_TOKEN);
};

/**
 * Get the pending invite table ID from localStorage
 */
export const getPendingInviteTableId = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.PENDING_INVITE_TABLE_ID);
};

/**
 * Check if there's a pending invite
 */
export const hasPendingInvite = (): boolean => {
  return !!getPendingInviteToken();
};

/**
 * Clear pending invite from localStorage
 */
export const clearPendingInvite = (): void => {
  localStorage.removeItem(STORAGE_KEYS.PENDING_INVITE_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.PENDING_INVITE_TABLE_ID);
  console.log('🗑️ [INVITE] Cleared pending invite');
};

/**
 * Store post-onboarding redirect URL
 * Used when new users need to complete onboarding before joining a game
 */
export const storePostOnboardingRedirect = (redirectUrl: string): void => {
  localStorage.setItem(STORAGE_KEYS.POST_ONBOARDING_REDIRECT, redirectUrl);
  console.log('💾 [INVITE] Stored post-onboarding redirect:', redirectUrl);
};

/**
 * Get and clear post-onboarding redirect URL
 */
export const getAndClearPostOnboardingRedirect = (): string | null => {
  const redirect = localStorage.getItem(STORAGE_KEYS.POST_ONBOARDING_REDIRECT);
  if (redirect) {
    localStorage.removeItem(STORAGE_KEYS.POST_ONBOARDING_REDIRECT);
    console.log('🔄 [INVITE] Using post-onboarding redirect:', redirect);
  }
  return redirect;
};

/**
 * Process pending invite after login
 * - Validates and uses the token
 * - Returns redirect URL for game page
 * - Handles private table access
 */
export const processPendingInviteAfterLogin = async (): Promise<{
  redirectUrl: string | null;
  isNewUser?: boolean;
}> => {
  const token = getPendingInviteToken();
  const tableId = getPendingInviteTableId();
  
  if (!token || !tableId) {
    return { redirectUrl: null };
  }

  try {
    // Use the invite token to get access
    const result = await consumeInviteToken(token);
    
    if (result.success && result.tableId) {
      // Store private code in session storage if needed
      if (result.privateCode) {
        sessionStorage.setItem(`privateCode_${result.tableId}`, result.privateCode);
      }
      
      // Clear the pending invite
      clearPendingInvite();
      
      // Return redirect URL
      return {
        redirectUrl: `/game/${result.tableId}?invite=${token}`,
      };
    } else {
      console.warn('⚠️ [INVITE] Token validation failed:', result.error);
      clearPendingInvite();
      return { redirectUrl: null };
    }
  } catch (error) {
    console.error('❌ [INVITE] Error processing pending invite:', error);
    clearPendingInvite();
    return { redirectUrl: null };
  }
};

// ============================================================================
// IN-APP INVITES (Direct invites to specific users)
// ============================================================================

/**
 * Send an invite to a specific user
 * - Checks rate limiting
 * - Checks if user is already at table
 * - Stores offline notification request
 */
export const sendInvite = async (
  fromUID: string,
  fromUserName: string,
  toUID: string,
  tableId: string,
  tableName: string
): Promise<string> => {
  try {
    // Rate limit check
    const rateLimitKey = `${fromUID}_${toUID}`;
    const lastInviteTime = lastInviteTimes.get(rateLimitKey) || 0;
    const timeSinceLastInvite = Date.now() - lastInviteTime;
    
    if (timeSinceLastInvite < INVITE_COOLDOWN_MS) {
      const waitTime = Math.ceil((INVITE_COOLDOWN_MS - timeSinceLastInvite) / 1000);
      throw new Error(`Please wait ${waitTime} seconds before sending another invite to this user`);
    }
    
    // Check for existing pending invite
    const existingInviteQuery = query(
      collection(db, 'invites'),
      where('fromUID', '==', fromUID),
      where('toUID', '==', toUID),
      where('tableId', '==', tableId),
      where('status', '==', 'pending')
    );
    
    const existingInvites = await getDocs(existingInviteQuery);
    const now = new Date();
    
    for (const docSnap of existingInvites.docs) {
      const data = docSnap.data();
      if (data.expiresAt && data.expiresAt.toDate() > now) {
        throw new Error('You already have a pending invite to this user for this table');
      }
    }
    
    // Check if user is already at the table
    const tableDoc = await getDoc(doc(db, 'tables', tableId));
    if (tableDoc.exists()) {
      const tableData = tableDoc.data();
      const playersAtTable = tableData.players || [];
      const gameStatePlayers = tableData.gameState?.players || [];
      
      const isAlreadyAtTable = playersAtTable.includes(toUID) || 
                               gameStatePlayers.some((p: any) => p.id === toUID);
      
      if (isAlreadyAtTable) {
        throw new Error('This user is already at the table');
      }
    }

    // Check if recipient is online
    const recipientOnline = await isUserOnline(toUID);
    
    // Get recipient email for offline notification
    let toEmail: string | null = null;
    if (!recipientOnline) {
      toEmail = await getUserEmail(toUID);
    }

    const expiresAt = new Date();
    expiresAt.setMinutes(expiresAt.getMinutes() + 5);
    
    const inviteData: any = {
      fromUID,
      fromUserName,
      toUID,
      tableId,
      tableName,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(expiresAt),
      recipientWasOffline: !recipientOnline,
      emailSent: false,
      smsSent: false,
    };

    if (toEmail && !recipientOnline) {
      inviteData.toEmail = toEmail;
      inviteData.needsEmailNotification = true;
    }

    const docRef = await addDoc(collection(db, 'invites'), inviteData);
    
    // Update rate limit tracker
    lastInviteTimes.set(rateLimitKey, Date.now());
    
    console.log('📧 [INVITE] Sent invite to', toUID, recipientOnline ? '(online)' : '(offline)');
    return docRef.id;
  } catch (error) {
    console.error('Error sending invite:', error);
    throw error;
  }
};

export const acceptInvite = async (inviteId: string): Promise<string> => {
  try {
    const inviteRef = doc(db, 'invites', inviteId);
    const inviteSnap = await getDoc(inviteRef);
    
    if (!inviteSnap.exists()) throw new Error('Invite not found');
    
    const invite = inviteSnap.data();
    
    if (invite.status !== 'pending') throw new Error('Invite is no longer pending');
    
    if (invite.expiresAt && invite.expiresAt.toDate() < new Date()) {
      await updateDoc(inviteRef, { status: 'expired' });
      throw new Error('Invite has expired');
    }
    
    await updateDoc(inviteRef, { status: 'accepted' });
    return invite.tableId;
  } catch (error) {
    console.error('Error accepting invite:', error);
    throw error;
  }
};

export const declineInvite = async (inviteId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'invites', inviteId), { status: 'declined' });
  } catch (error) {
    console.error('Error declining invite:', error);
    throw error;
  }
};

export const cancelInvite = async (inviteId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'invites', inviteId));
  } catch (error) {
    console.error('Error cancelling invite:', error);
    throw error;
  }
};

export const getPendingInvites = async (uid: string): Promise<TableInvite[]> => {
  try {
    const q = query(
      collection(db, 'invites'),
      where('toUID', '==', uid),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const invites: TableInvite[] = [];
    const now = new Date();
    
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.expiresAt && data.expiresAt.toDate() > now) {
        invites.push({ id: docSnap.id, ...data } as TableInvite);
      }
    });
    
    return invites;
  } catch (error) {
    console.error('Error getting pending invites:', error);
    return [];
  }
};

export const subscribeToPendingInvites = (
  uid: string,
  callback: (invites: TableInvite[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'invites'),
    where('toUID', '==', uid),
    where('status', '==', 'pending')
  );
  
  return onSnapshot(q, (snapshot) => {
    const invites: TableInvite[] = [];
    const now = new Date();
    
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.expiresAt && data.expiresAt.toDate() > now) {
        invites.push({ id: docSnap.id, ...data } as TableInvite);
      }
    });
    
    callback(invites);
  }, () => callback([]));
};

export const getSentInvites = async (uid: string): Promise<TableInvite[]> => {
  try {
    const q = query(
      collection(db, 'invites'),
      where('fromUID', '==', uid),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const invites: TableInvite[] = [];
    const now = new Date();
    
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.expiresAt && data.expiresAt.toDate() > now) {
        invites.push({ id: docSnap.id, ...data } as TableInvite);
      }
    });
    
    return invites;
  } catch (error) {
    console.error('Error getting sent invites:', error);
    return [];
  }
};

export const subscribeToSentInvites = (
  uid: string,
  callback: (invites: TableInvite[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'invites'),
    where('fromUID', '==', uid),
    where('status', '==', 'pending')
  );
  
  return onSnapshot(q, (snapshot) => {
    const invites: TableInvite[] = [];
    const now = new Date();
    
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.expiresAt && data.expiresAt.toDate() > now) {
        invites.push({ id: docSnap.id, ...data } as TableInvite);
      }
    });
    
    callback(invites);
  }, () => callback([]));
};

export const markEmailSent = async (inviteId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'invites', inviteId), { emailSent: true });
  } catch (error) {
    console.error('Error marking email sent:', error);
  }
};

/**
 * Check if user can send invite (rate limit check)
 */
export const canSendInvite = (fromUID: string, toUID: string): { canSend: boolean; waitTime: number } => {
  const rateLimitKey = `${fromUID}_${toUID}`;
  const lastInviteTime = lastInviteTimes.get(rateLimitKey) || 0;
  const timeSinceLastInvite = Date.now() - lastInviteTime;
  
  if (timeSinceLastInvite < INVITE_COOLDOWN_MS) {
    const waitTime = Math.ceil((INVITE_COOLDOWN_MS - timeSinceLastInvite) / 1000);
    return { canSend: false, waitTime };
  }
  
  return { canSend: true, waitTime: 0 };
};

export const clearRateLimit = (fromUID: string, toUID: string): void => {
  const rateLimitKey = `${fromUID}_${toUID}`;
  lastInviteTimes.delete(rateLimitKey);
};

// ============================================================================
// LEGACY FUNCTIONS (kept for backwards compatibility)
// ============================================================================

/**
 * @deprecated Use generateSecureInviteToken instead
 */
export const generateTableShareLink = (
  tableId: string,
  _tableName: string,
  appUrl: string = window.location.origin
): string => {
  return `${appUrl}/game/${tableId}`;
};

/**
 * @deprecated Use copySecureInviteLink instead
 */
export const copyInviteLink = async (tableId: string, tableName: string): Promise<boolean> => {
  // Redirect to secure method
  return copySecureInviteLink(tableId, tableName);
};

/**
 * @deprecated Use shareSecureInviteNative instead
 */
export const shareInviteNative = async (tableId: string, tableName: string): Promise<boolean> => {
  // Redirect to secure method
  return shareSecureInviteNative(tableId, tableName);
};

// ============================================================================
// SERVICE EXPORT
// ============================================================================

export const inviteService = {
  // Secure token-based invites
  generateSecureInviteToken,
  validateInviteToken,
  consumeInviteToken,
  copySecureInviteLink,
  shareSecureInviteNative,
  
  // Pending invite storage
  storePendingInvite,
  getPendingInviteToken,
  getPendingInviteTableId,
  hasPendingInvite,
  clearPendingInvite,
  storePostOnboardingRedirect,
  getAndClearPostOnboardingRedirect,
  processPendingInviteAfterLogin,
  
  // In-app invites
  sendInvite,
  acceptInvite,
  declineInvite,
  cancelInvite,
  getPendingInvites,
  subscribeToPendingInvites,
  getSentInvites,
  subscribeToSentInvites,
  markEmailSent,
  canSendInvite,
  clearRateLimit,
  
  // Legacy (backwards compatible)
  generateTableShareLink,
  copyInviteLink,
  shareInviteNative,
};

export default inviteService;